/**
 * Number Formatting Utilities
 * ============================
 * Consistent formatting across all Corvana dashboards
 */

/**
 * Format a number as Australian currency ($X,XXX.XX)
 * @param {number} value - The value to format
 * @param {boolean} showCents - Whether to show cents (default: false for large numbers)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, showCents = false) => {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value;
  if (isNaN(num)) return '$0';
  
  const absVal = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  
  if (absVal >= 1000000) {
    return `${sign}$${(absVal / 1000000).toFixed(1)}M`;
  }
  if (absVal >= 1000 && !showCents) {
    return `${sign}$${Math.round(absVal).toLocaleString('en-AU')}`;
  }
  if (showCents) {
    return `${sign}$${absVal.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${sign}$${Math.round(absVal).toLocaleString('en-AU')}`;
};

/**
 * Format a number with commas (X,XXX)
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '0';
  return num.toLocaleString('en-AU', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

/**
 * Format a percentage value
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0%';
  return `${num.toFixed(decimals)}%`;
};

/**
 * Format a metric value based on its type/name
 * Automatically determines formatting from the metric name
 * @param {number|string} value - The value to format
 * @param {string} metricName - The name of the metric (used to determine formatting)
 * @returns {string} Formatted value
 */
export const formatMetricValue = (value, metricName = '') => {
  if (value === null || value === undefined) return '-';
  
  const name = metricName.toLowerCase();
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[$,%]/g, '')) : value;
  
  // Already formatted
  if (typeof value === 'string' && (value.startsWith('$') || value.endsWith('%') || value.includes(','))) {
    return value;
  }
  
  // Currency indicators
  if (name.includes('revenue') || name.includes('profit') || name.includes('cost') ||
      name.includes('sales') || name.includes('cash') || name.includes('billings') ||
      name.includes('commission') || name.includes('value') || name.includes('margin') ||
      name.includes('income') || name.includes('spend') || name.includes('price') ||
      name.includes('budget') || name.includes('fee') || name.includes('adr') ||
      name.includes('mrr') || name.includes('arr') || name.includes('fum') ||
      name.includes('wip') || name.includes('gci') || name.includes('wage') ||
      name.includes('atv') || name.includes('basket') || name.includes('ticket') ||
      name.includes('check') || name.includes('impact')) {
    return formatCurrency(numValue);
  }
  
  // Percentage indicators
  if (name.includes('%') || name.includes('rate') || name.includes('margin') ||
      name.includes('conversion') || name.includes('utilisation') || name.includes('utilization') ||
      name.includes('retention') || name.includes('occupancy') || name.includes('fill') ||
      name.includes('penetration') || name.includes('shrinkage') || name.includes('yield') ||
      name.includes('oee') || name.includes('nps') || name.includes('score')) {
    return formatPercent(numValue);
  }
  
  // Time indicators
  if (name.includes('time') || name.includes('days') || name.includes('minutes') || name.includes('hours')) {
    if (name.includes('days')) return `${formatNumber(numValue)} days`;
    if (name.includes('hours')) return `${formatNumber(numValue)} hrs`;
    if (name.includes('minutes') || name.includes('min')) return `${formatNumber(numValue)} min`;
    return formatNumber(numValue);
  }
  
  // Default: format with commas
  if (!isNaN(numValue)) {
    return formatNumber(numValue);
  }
  
  return String(value);
};

/**
 * Format a compact number (e.g., 1.2K, 3.5M)
 * @param {number} value - The value to format
 * @returns {string} Compact formatted number
 */
export const formatCompact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  
  const absVal = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  
  if (absVal >= 1000000) return `${sign}${(absVal / 1000000).toFixed(1)}M`;
  if (absVal >= 1000) return `${sign}${(absVal / 1000).toFixed(1)}K`;
  return `${sign}${num.toFixed(0)}`;
};
